import { Application } from '@goodboydigital/astro';
import { SystemInterface } from '@goodboydigital/odie';
import SoundBoy, { SoundInstance } from '@goodboydigital/soundboy';
import { Signal } from 'typed-signals';

import { Maths } from '../../utils/Maths';
import { App, Shared } from './../../App';

export interface VehicleAudioMeta
{
    chassisId: string,
    wheelLeftId: string,
    wheelRightId: string,
}

interface VehicleAudio
{
    baseLoop: SoundInstance,
    rev: SoundInstance,
    brake: SoundInstance,
    release: SoundInstance,
    gearing: number,
    minLoopSpeed: number,
    maxLoopSpeed: number,
    pedalModifier: number,
}

interface WheelAudio
{
    brake: string,
}

interface ChassisAudio
{
    rev: string,
    release: string,
    baseLoop: string,
    gearing?: number,
    minLoopSpeed?: number,
    maxLoopSpeed?: number,
    pedalModifier?: number,
}

export class SoundSystem implements SystemInterface
{
    public app: App;
    public muteChanged: Signal<(state: boolean) => void>;

    protected vehicleAudio: Record<string, VehicleAudio>;

    private _chassisAudioHash: Record<string, ChassisAudio>;
    private _wheelAudioHash: Record<string, WheelAudio>;

    private _currentMusic: string;
    private _currentGameMusicIndex: number;
    private _gameMusic: string[];
    
    private _meta!: VehicleAudioMeta;

    constructor(app: Application)
    {
        this.muteChanged = new Signal<(state: boolean) => void>();
        this.app = app as App;

        this.vehicleAudio = {};
        this._wheelAudioHash = {};
        this._chassisAudioHash = {};
        this._currentMusic = '';
        this._gameMusic = [];
        this._currentGameMusicIndex = -1;

        SoundBoy.volume = 0.7;
        this._populateSoundHash();
    }

    public registerVehicle(vehicleId: string, meta: VehicleAudioMeta): void
    {
        this._meta = meta;
        
        const baseLoop = SoundBoy.playSfx(
            (this._chassisAudioHash[meta.chassisId] as ChassisAudio).baseLoop,
            { volume: 0, loops: true },
        );
        const rev = SoundBoy.playSfx(
            (this._chassisAudioHash[meta.chassisId] as ChassisAudio).rev,
            { volume: 0 },
        );
        const release = SoundBoy.playSfx(
            (this._chassisAudioHash[meta.chassisId] as ChassisAudio).release,
            { volume: 0 },
        );
        const brake = SoundBoy.playSfx(
            (this._wheelAudioHash[meta.wheelLeftId] as WheelAudio).brake,
            { volume: 0 },
        );

        const gearing = (this._chassisAudioHash[meta.chassisId] as ChassisAudio).gearing || 0.15;
        const minLoopSpeed = (this._chassisAudioHash[meta.chassisId] as ChassisAudio).minLoopSpeed || 1;
        const maxLoopSpeed = (this._chassisAudioHash[meta.chassisId] as ChassisAudio).maxLoopSpeed || 3.3;
        const pedalModifier = (this._chassisAudioHash[meta.chassisId] as ChassisAudio).pedalModifier || 0.7;

        this.vehicleAudio[vehicleId] = {
            baseLoop,
            rev,
            brake,
            release,
            gearing,
            minLoopSpeed,
            maxLoopSpeed,
            pedalModifier,
        };
    }

    // RACE EVENTS

    public startRace(): void
    {
        Object.values(this.vehicleAudio).forEach((val) =>
        {
            // restart base loops
            val.baseLoop.restart();
            val.baseLoop.volume = 0.5;
            val.baseLoop.fadeIn(0.3);
        });
    }

    public accelerating(vehicleId: string): void
    {
        const sound = this.vehicleAudio[vehicleId];

        if (!sound) return;

        if (sound.rev.completed)
        {
            sound.rev = SoundBoy.playSfx(
                (this._chassisAudioHash[this._meta.chassisId] as ChassisAudio).rev,
                { volume: 0.5 },
            );
        }
    }

    public release(vehicleId: string, magnitude: number): void
    {
        const sound = this.vehicleAudio[vehicleId];

        if (!sound) return;

        if (sound.release.completed)
        {
            this.vehicleAudio[vehicleId].release = SoundBoy.playSfx(
                (this._chassisAudioHash[this._meta.chassisId] as ChassisAudio).release,
                { volume: 0.5 * magnitude },
            );
        }
    }

    public updateBaseLoopTone(vehicleId: string, velocity: number, pedalModifier: number): void
    {
        const sound = this.vehicleAudio[vehicleId];

        if (!sound) return;

        const clampedVel = Maths.clamp(velocity * sound.gearing, sound.minLoopSpeed, sound.maxLoopSpeed);
        const clampedPedalMod = Maths.clamp(pedalModifier, 0, 0.5);

        sound.baseLoop.rate = clampedVel + clampedPedalMod;
    }

    public braking(vehicleId: string): void
    {
        const sound = this.vehicleAudio[vehicleId];

        if (!sound) return;

        if (sound.brake.completed)
        {
            sound.brake = SoundBoy.playSfx(
                (this._wheelAudioHash[this._meta.wheelLeftId] as WheelAudio).brake,
                { volume: 0.6 },
            );
        }
    }

    public onPause():void
    {
        Object.values(this.vehicleAudio).forEach((val) =>
        {
            val.baseLoop.pause();
            // val.brake.pause();
            // val.release.pause();
            // val.rev.pause();
        });
    }

    public onResume():void
    {
        Object.values(this.vehicleAudio).forEach((val) =>
        {
            val.baseLoop.resume();
            //val.brake.resume();
            //val.release.resume();
            //val.rev.resume();
        });
    }

    public raceEnd(): void
    {
        Object.values(this.vehicleAudio).forEach((val) =>
        {
            val.baseLoop.fadeOut(0.5, () =>
            {
                val.baseLoop.stop();
                this.flush();
            });
            val.brake.fadeOut(0.5, () => val.brake.stop());
            val.release.fadeOut(0.5, () => val.release.stop());
            val.rev.fadeOut(0.5, () => val.rev.stop());
        });
        this.playSfx('race-end', 0.8);
    }

    public playRaceMusic(): void
    {
        // if race music is already playing, return
        if (this._gameMusic.includes(this._currentMusic)) return;

        this.playNextGameTrack();
    }

    public playNextGameTrack(): void
    {
        this._currentGameMusicIndex = (this._currentGameMusicIndex + 1) % this._gameMusic.length;

        SoundBoy.playMusic(this._gameMusic[this._currentGameMusicIndex], {
            volume: 0.65,
            callback: () => this.playNextGameTrack(),
        });

        this._currentMusic = this._gameMusic[this._currentGameMusicIndex];
    }

    // DEFAULT SFX AND MUSIC METHODS

    public playMusic(id: string): void
    {
        if (this._currentMusic === id) return;

        this._currentMusic = id;

        SoundBoy.playMusic(id, { volume: 0.65 });
    }

    public playSfx(id: string, volume = 0.7): SoundInstance
    {
        return SoundBoy.playSfx(id, { volume });
    }

    public flush(): void
    {
        this.vehicleAudio = {};
    }

    private _populateSoundHash(): void
    {
        for (const f in Shared.assets.data)
        {
            const isWheel = f.startsWith('wheel-');
            const isChassis = f.startsWith('chassis-');

            if (isWheel)
            {
                const data = Shared.assets.getData<Record<string, any>>(f);

                this._wheelAudioHash[f.replace('wheel-', '')] = data.audio
                    ? data.audio as WheelAudio
                    : { brake: 'brake-01' };
            }
            if (isChassis)
            {
                const data = Shared.assets.getData<Record<string, any>>(f);

                this._chassisAudioHash[f.replace('chassis-', '')] = data.audio
                    ? data.audio as ChassisAudio
                    : {
                        rev: 'rev-01',
                        release: 'accelerate-release',
                        baseLoop: 'engine-loop',
                    };
            }
        }

        this._gameMusic = Shared.assets.getData<Record<string, any>>('game-music').slice(0);
    }

    public update(): void
    {
        // Needed for valid system interface
    }
}
